import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux';
import {
  hideLoading,
  showLoading,
} from 'src/redux/Components/Loading/LoadingSlice';

/**
 * Custom hook for managing loading state in a Redux application.
 *
 * This hook provides functions to start and stop loading, as well as
 * the current loading state. It integrates with Redux to manage the
 * loading state across the application.
 *
 * @returns {Object} An object containing the loading state and functions
 *                   to control it.
 * @returns {boolean} isLoading - A boolean indicating whether loading is active.
 * @returns {function} startLoading - A function to initiate loading.
 * @returns {function} stopLoading - A function to stop loading.
 *
 * @example
 * const MyComponent = () => {
 *   const { isLoading, startLoading, stopLoading } = useLoading();
 *
 *   const handleClick = () => {
 *     startLoading();
 *     // Perform some asynchronous action
 *     stopLoading();
 *   };
 *
 *   return (
 *     <div>
 *       {isLoading ? <p>Loading...</p> : <p>Content Loaded</p>}
 *       <button onClick={handleClick}>Load Content</button>
 *     </div>
 *   );
 * };
 */
const useLoading = () => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state: RootState) => state.loading);
  const startLoading = () => dispatch(showLoading());
  const stopLoading = () => dispatch(hideLoading());

  return {
    isLoading,
    startLoading,
    stopLoading,
  };
};

export default useLoading;
